import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ReturnDetail, ReturnOutcome } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";
import { conditionLabel } from "../../utils/returnsRules";
import { trackingLink } from "../../utils/trackingLink";
import { ChannelBadge, Chip, ChipTone, FieldRow, PanelCard, PlateChip } from "../messagesInbox/InboxBits";
import ClickToCopy from "../shared/ClickToCopy";

const OUTCOME_LABELS: Record<ReturnOutcome, string> = {
  HoldingBay: "Holding bay · locate later",
  BackToStock: "Back to stock",
  QualityControl: "Quality control",
  Scrap: "Scrap",
  PutAwayInYard: "Put away in Wilma Yard",
};

const outcomeTone: Record<ReturnOutcome, ChipTone> = { HoldingBay: "grey", BackToStock: "green", QualityControl: "amber", Scrap: "red", PutAwayInYard: "grey" };

function stockTone(status: string | null): ChipTone {
  if (!status) return "grey";
  if (/in stock|dismantled/i.test(status)) return "green";
  if (/damaged|scrap/i.test(status)) return "red";
  if (/on the car/i.test(status)) return "accent";
  return "grey";
}

function PartCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const p = detail.part;
  if (!p) {
    // Wilma tries again hourly; until then eBay's item and order are what the team can look the part up by.
    return (
      <section className="flex flex-col gap-2 rounded-xl border border-dashed border-gray-300 bg-white p-4 text-[13px] text-gray-500">
        <p>{t("This return isn't linked to a part in Wilma yet. Wilma checks again every hour.")}</p>
        {(detail.ebayItemId || detail.orderNumber) && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
            {detail.ebayItemId && (
              <>
                <dt>{t("eBay item")}</dt>
                <dd className="text-gray-900">
                  <ClickToCopy text={detail.ebayItemId} />
                </dd>
              </>
            )}
            {detail.orderNumber && (
              <>
                <dt>{t("Order")}</dt>
                <dd className="text-gray-900">
                  <ClickToCopy text={detail.orderNumber} />
                </dd>
              </>
            )}
          </dl>
        )}
        {detail.links.ebayListingUrl && (
          <a href={detail.links.ebayListingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary-700 hover:underline">
            {t("View listing on eBay")} <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </a>
        )}
      </section>
    );
  }
  const image = p.imageUrls?.[0] ?? p.imageUrl;
  const href = detail.links.partUrl ?? `/stock/${p.id}`;
  const photos = p.imageUrls?.length || p.photoCount;
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <div className="flex gap-3 p-3">
        <Link to={href} title={t("Open part in Wilma")} className="shrink-0 hover:opacity-80">
          {image ? (
            <img src={image} alt="" className="h-[84px] w-[84px] rounded-lg bg-gray-100 object-cover" />
          ) : (
            <span className="flex h-[84px] w-[84px] flex-col items-center justify-center rounded-lg bg-gray-100 text-gray-400">
              <PhotoIcon className="h-7 w-7" />
              <span className="mt-1 text-[10px]">{t("{{count}} photos", { count: photos })}</span>
            </span>
          )}
        </Link>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Link to={href} className="text-[13px] font-semibold leading-snug text-gray-900 hover:text-primary-700 hover:underline">
            {p.title}
          </Link>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[15px] font-bold text-gray-900">{PriceFormat(p.price)}</span>
            {p.condition && <Chip>{p.condition}</Chip>}
            {(p.stockStatus || p.status) && <Chip tone={stockTone(p.stockStatus ?? p.status)}>{(p.stockStatus ?? p.status) as string}</Chip>}
          </div>
          <p className="text-[12px] text-gray-700">
            {t("Tag")} <span className="font-semibold text-gray-900">{p.tagNumber ?? "—"}</span>
            {p.lastLocation ? ` · ${t("was {{where}}", { where: p.lastLocation })}` : p.location ? ` · ${p.location}` : ""}
          </p>
        </div>
      </div>
    </section>
  );
}

function OrderCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const o = detail.order;
  if (!o) return null;
  return (
    <PanelCard title={t("Order")}>
      <FieldRow label={t("Order no.")}>
        <span className="font-mono">{o.orderNumber}</span>
      </FieldRow>
      <FieldRow label={t("Date")}>{formatDateTime(o.orderedAt, INBOX_DAY_TIME)}</FieldRow>
      <FieldRow label={t("Paid")}>
        <strong className="font-semibold">{PriceFormat(o.pricePaid)}</strong> + {PriceFormat(o.postage)} {t("postage")}
        {o.paymentStatus && <span className={classNames("ml-1 font-semibold", /paid|complete/i.test(o.paymentStatus) ? "text-green-700" : "text-amber-700")}>· {o.paymentStatus}</span>}
      </FieldRow>
      {o.refundedSoFar > 0 && <FieldRow label={t("Refunded so far")}>{PriceFormat(o.refundedSoFar)}</FieldRow>}
      {o.invoiceNumber && (
        <FieldRow label={t("Invoice")}>{detail.links.invoiceUrl ? <Link to={detail.links.invoiceUrl} className="text-primary-700 hover:underline">{o.invoiceNumber}</Link> : o.invoiceNumber}</FieldRow>
      )}
      {(o.deliveryName || o.deliveryAddress) && (
        <FieldRow label={t("Delivery")}>
          <span className="block whitespace-pre-line">{[o.deliveryName, ...(o.deliveryAddress ?? [])].filter(Boolean).join("\n")}</span>
        </FieldRow>
      )}
    </PanelCard>
  );
}

function DispatchCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const o = detail.order;
  if (!o) return null;
  const href = trackingLink({ trackingNumber: o.trackingNumber ?? undefined, courier: o.courier ?? undefined });
  const who = (name: string | null, at: string | null) => [name, at ? formatDateTime(at, INBOX_DAY_TIME) : null].filter(Boolean).join(" · ") || "—";
  return (
    <PanelCard title={t("Dispatch")}>
      <FieldRow label={t("Picked")}>{who(o.pickedBy, o.pickedAt)}</FieldRow>
      <FieldRow label={t("Label printed")}>{o.labelPrintedAt ? formatDateTime(o.labelPrintedAt, INBOX_DAY_TIME) : "—"}</FieldRow>
      <FieldRow label={t("Shipped")}>
        {o.shippedAt ? (
          <>
            {[o.shippedBy, o.courier].filter(Boolean).join(" · ")}
            {o.shippedBy || o.courier ? " · " : ""}
            {formatDateTime(o.shippedAt, INBOX_DAY_TIME)}
          </>
        ) : o.isCollection ? (
          t("Collection")
        ) : (
          t("Not shipped")
        )}
      </FieldRow>
      <FieldRow label={t("Tracking")}>
        {o.trackingNumber ? (
          href ? (
            <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[12px] font-medium text-primary-700 hover:underline">
              {o.trackingNumber} <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </a>
          ) : (
            <ClickToCopy text={o.trackingNumber} textClassName="font-mono text-[12px]" />
          )
        ) : (
          "—"
        )}
      </FieldRow>
      <FieldRow label={t("Delivered")}>
        {detail.deliveredAt ? (
          <>
            <strong className="font-semibold">{formatDateTime(detail.deliveredAt, INBOX_DAY_TIME)}</strong> <span className="text-gray-500">· {t("eBay window starts")}</span>
          </>
        ) : detail.withinReturnWindow === null ? (
          <span className="text-gray-500">{t("No delivered date")}</span>
        ) : (
          <span className={detail.withinReturnWindow ? "text-gray-700" : "text-red-700"}>{detail.withinReturnWindow ? t("eBay says inside the window") : t("eBay says outside the window")}</span>
        )}
      </FieldRow>
    </PanelCard>
  );
}

function DonorCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const v = detail.donor;
  if (!v) return null;
  return (
    <section className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
      {v.vrm && <PlateChip reg={v.vrm} />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-semibold text-gray-900">
          {t("Donor")} · {[v.make, v.model, v.variant, v.year].filter(Boolean).join(" ")}
        </p>
        <p className="truncate text-[11px] text-gray-500">
          {[v.engineCode, v.fuel, v.gearbox].filter(Boolean).join(" · ")}
          {v.mileage ? ` · ${v.mileage.toLocaleString("en-GB")} mi` : ""}
        </p>
      </div>
      <Link to={`/vehicles/${v.id}`} className="shrink-0 text-[12px] font-semibold text-primary-700 hover:underline">
        {t("View")} →
      </Link>
    </section>
  );
}

function BuyerCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const b = detail.buyer;
  const about = [b.memberSince ? t("Member since {{when}}", { when: b.memberSince }) : null, b.feedbackPct !== null ? `${b.feedbackPct}%` : null].filter(Boolean).join(" · ");
  return (
    <PanelCard title={t("Buyer")}>
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="font-semibold text-gray-900">{b.username}</span>
        {detail.channel !== "counter" && <ChannelBadge channel={detail.channel} short />}
        {about && <span className="text-gray-500">{about}</span>}
      </div>
      <div className="mt-1 flex gap-4 text-[12px] text-gray-700">
        <span>{b.orders === 1 ? t("{{count}} order", { count: 1 }) : t("{{count}} orders", { count: b.orders })} · {PriceFormat(b.spend)}</span>
        <span>{b.previousReturns === 1 ? t("{{count}} previous return", { count: 1 }) : t("{{count}} previous returns", { count: b.previousReturns })}</span>
      </div>
    </PanelCard>
  );
}

function InspectionCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const i = detail.inspection;
  if (!i) {
    return (
      <section className="flex flex-col gap-1 rounded-xl border border-dashed border-gray-300 bg-white p-3 text-[12px] text-gray-500">
        <h2 className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">{t("Received and checked")}</h2>
        <span>{t("Not checked yet. When the part is back, find the return (scan or type its Tag no. or tracking on the Returns list) and fill in Received and checked.")}</span>
      </section>
    );
  }
  const yesNo = (v: boolean | null) => (v === null ? t("N/A") : v ? t("Yes") : t("No"));
  const tone = (v: boolean | null): string => (v === false ? "font-semibold text-red-700" : "text-gray-900");
  return (
    <PanelCard title={t("Received and checked")}>
      <FieldRow label={t("Correct part")}>
        <span className={tone(i.correctPart)}>{yesNo(i.correctPart)}</span>
      </FieldRow>
      <FieldRow label={t("Part no. matches")}>
        <span className={tone(i.partNumberMatches)}>{yesNo(i.partNumberMatches)}</span>
      </FieldRow>
      <FieldRow label={t("Engine code matches")}>
        <span className={tone(i.engineCodeMatches)}>{yesNo(i.engineCodeMatches)}</span>
      </FieldRow>
      <FieldRow label={t("Same condition")}>
        <span className={tone(i.sameCondition)}>
          {yesNo(i.sameCondition)}
          {i.difference ? ` — "${i.difference}"` : ""}
        </span>
      </FieldRow>
      <FieldRow label={t("Condition band")}>
        <Chip tone={i.condition === "Excellent" || i.condition === "Good" ? "green" : i.condition === "Fair" ? "amber" : "red"}>{conditionLabel(i.condition)}</Chip>
      </FieldRow>
      <FieldRow label={t("Outcome")}>
        <Chip tone={outcomeTone[i.outcome]}>{t(OUTCOME_LABELS[i.outcome])}</Chip>
      </FieldRow>
      {(i.locatedBin || i.location) && (
        <FieldRow label={t("Located")}>
          {i.locatedBin ?? i.location}
          {i.locatedAt ? ` · ${formatDateTime(i.locatedAt, INBOX_DAY_TIME)}` : ""}
          {i.relistedAt ? ` · ${t("relisted")}` : ""}
        </FieldRow>
      )}
      {i.note && <FieldRow label={t("Note")}>{i.note}</FieldRow>}
      <FieldRow label={t("By")}>{[i.by, formatDateTime(i.at, INBOX_DAY_TIME)].filter(Boolean).join(" · ")}</FieldRow>
      {i.photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {i.photos.map((p) => (
            <a key={p.url} href={p.url} target="_blank" rel="noreferrer" title={p.kind} className="block h-16 w-16 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
              <img loading="lazy" src={p.url} alt={p.name} className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      )}
    </PanelCard>
  );
}

/** The right-hand column: the same part / order / donor / buyer cards as the inbox, plus the check once it's done. */
export default function ReturnContextPanel({ detail }: { detail: ReturnDetail }) {
  return (
    <aside className="flex w-full shrink-0 flex-col gap-2.5 lg:w-[392px]">
      <PartCard detail={detail} />
      <OrderCard detail={detail} />
      <DispatchCard detail={detail} />
      <DonorCard detail={detail} />
      <BuyerCard detail={detail} />
      <InspectionCard detail={detail} />
    </aside>
  );
}
